import { Shield, ExternalLink, LogOut, CheckCircle2, WifiOff, RefreshCw, Store, LayoutDashboard } from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { Badge } from '../../components/ui/Badge.jsx';

export function AdminHeader({
  activeTenant,
  tenants = [],
  onSelectTenant,
  onNavigate,
  onLogout,
  connectionStatus = 'connected',
  onRefresh,
  refreshing = false,
  role = null,
}) {
  const isTenantAdmin = role === 'tenant_admin';

  return (
    <header className="border-b border-[var(--border)] bg-[var(--surface,white)] shadow-xs">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Left: Brand / Title */}
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--primary)] text-white shadow-xs">
              <Shield className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-base font-bold text-[var(--foreground)]">
                  {isTenantAdmin ? (activeTenant?.name ? `${activeTenant.name} – Verwaltung` : 'Shop-Verwaltung') : 'ASL Control-Plane'}
                </span>
                <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 border-[var(--border)] text-[var(--secondary)] bg-[var(--background)]">
                  {isTenantAdmin ? 'Partner-Admin' : 'System-Admin'}
                </Badge>
              </div>
              <p className="text-xs text-[var(--secondary)] hidden sm:block">
                {isTenantAdmin ? 'Inhalte, Theme & Sortiment anpassen' : 'Multi-Tenant Shop-Verwaltung & Administration'}
              </p>
            </div>
          </div>

          {/* Right: Tenant Quick-Switcher, Status & Actions */}
          <div className="flex items-center gap-3">
            {/* Connection Status Badge */}
            {connectionStatus === 'connected' ? (
              <div className="flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 border border-emerald-200">
                <span className="h-2 w-2 rounded-full bg-emerald-600 animate-pulse" aria-hidden="true" />
                <span className="hidden md:inline">Verbunden</span>
              </div>
            ) : connectionStatus === 'validating' ? (
              <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 border border-amber-200">
                <RefreshCw className="h-3 w-3 animate-spin" aria-hidden="true" />
                <span className="hidden md:inline">Prüfe...</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 border border-slate-200">
                <WifiOff className="h-3 w-3 text-slate-500" aria-hidden="true" />
                <span className="hidden md:inline">Offline-Modus</span>
              </div>
            )}

            {/* Tenant Selector Dropdown (for platform admin or multi-tenant switch) */}
            {tenants.length > 0 && !isTenantAdmin && (
              <div className="flex items-center gap-2">
                <label htmlFor="header-tenant-select" className="text-xs font-medium text-[var(--secondary)] hidden lg:inline">
                  Mandant:
                </label>
                <select
                  id="header-tenant-select"
                  aria-label="Aktiver Mandant"
                  value={activeTenant?.id || ''}
                  onChange={(e) => {
                    const chosen = tenants.find((t) => t.id === e.target.value);
                    if (chosen && onSelectTenant) onSelectTenant(chosen);
                  }}
                  className="rounded-[var(--radius-element,var(--radius))] border border-[var(--border)] bg-white px-2.5 py-1.5 text-xs font-medium text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring,var(--primary))]"
                >
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.slug})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Refresh button */}
            {onRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRefresh}
                disabled={refreshing}
                title="Daten aktualisieren"
                aria-label="Daten aktualisieren"
                className="h-8 w-8 p-0 cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--ring,var(--primary))]"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
              </Button>
            )}

            {/* Link to Betriebsleiter Hub (nur ASL-Voll-Admin, nicht für Demo-/Mandanten-Admins) */}
            {!isTenantAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onNavigate('/hub')}
                className="gap-1.5 text-xs border-[var(--primary)]/30 text-[var(--primary)] hover:bg-[var(--primary)]/5 cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--ring,var(--primary))]"
                data-testid="executive-hub-admin-trigger"
              >
                <LayoutDashboard className="h-3.5 w-3.5 text-[var(--primary)]" aria-hidden="true" />
                <span className="hidden sm:inline">Betriebsleiter Hub</span>
              </Button>
            )}

            {/* Link to Storefront */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onNavigate('/')}
              className="gap-1.5 text-xs border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--surface-container-low,#f1f5f9)] cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--ring,var(--primary))]"
            >
              <Store className="h-3.5 w-3.5 text-[var(--primary)]" aria-hidden="true" />
              <span className="hidden sm:inline">Zum Storefront</span>
            </Button>

            {/* Logout */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              title="Abmelden"
              aria-label="Abmelden"
              className="gap-1.5 text-xs text-rose-700 hover:bg-rose-50 hover:text-rose-800 cursor-pointer focus-visible:ring-2 focus-visible:ring-rose-500"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden md:inline">Abmelden</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}